import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["contentLink"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import _mainCommentIssueDataQuery from "./__generated__/mainCommentIssueDataQuery.graphql";
import React, { useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import { graphql, useLazyLoadQuery } from 'react-relay';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { Anchor, Inline, xcss } from '@atlaskit/primitives';
import { messages } from '../../../../index';
import { Experiences } from '../../../../types';
import { triggerJiraIssueCommentLoadedAnalytics, useCreateFireAnalyticsFromTrigger } from '../../../utils/analytics';
import { LinkType } from '../../../utils/analytics/types';
import { useExperienceTracker } from '../../../utils/experience-tracking/adapter/context';
import { ErrorBoundarySuspense } from '../../error-boundary-suspense';
import { ReactionsPlaceholder } from '../reactions-placeholder';
import { extractIssueKey } from '../utils';
import { JiraReactBar } from './jira-react-bar/main';
import JiraReplyBar from './jira-reply-button/main';
var fullThreadLinkStyles = xcss({
  font: "var(--ds-font-body-small, normal 400 11px/16px ui-sans-serif, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Ubuntu, \"Helvetica Neue\", sans-serif)",
  fontWeight: "var(--ds-font-weight-bold, 700)",
  textDecoration: 'none'
});
export var JiraReactReplyBar = function JiraReactReplyBar(_ref) {
  var contentLink = _ref.contentLink,
    others = _objectWithoutProperties(_ref, _excluded);
  var issueKey = extractIssueKey(contentLink);
  if (!issueKey) {
    return null;
  }
  return /*#__PURE__*/React.createElement(ErrorBoundarySuspense, {
    subjectId: "jiraReactReplyBar",
    loadingFallback: /*#__PURE__*/React.createElement(ReactionsPlaceholder, null),
    errorFallback: /*#__PURE__*/React.createElement(React.Fragment, null)
  }, /*#__PURE__*/React.createElement(InnerJiraReactReplyBar, _extends({
    issueKey: issueKey,
    contentLink: contentLink
  }, others)));
};
var InnerJiraReactReplyBar = function InnerJiraReactReplyBar(_ref2) {
  var _issueData$jira, _issueData$jira4, _issueData$jira5, _issueData$jira8, _issueData$jira9, _issueData$jira0;
  var issueKey = _ref2.issueKey,
    commentId = _ref2.commentId,
    cloudId = _ref2.cloudId,
    emojiProvider = _ref2.emojiProvider,
    experienceTrackingAttributes = _ref2.experienceTrackingAttributes,
    handleMarkAsRead = _ref2.handleMarkAsRead,
    isEditorShown = _ref2.isEditorShown,
    onCommentEditorShown = _ref2.onCommentEditorShown,
    onOpenFullThreadLinkClick = _ref2.onOpenFullThreadLinkClick,
    subProduct = _ref2.subProduct,
    contentLink = _ref2.contentLink;
  var experienceTracker = useExperienceTracker();
  var fireJiraIssueCommentLoaded = useCreateFireAnalyticsFromTrigger(triggerJiraIssueCommentLoadedAnalytics);
  var handleOpenFullThreadLinkClick = function handleOpenFullThreadLinkClick(clickType) {
    return function (event) {
      if (onOpenFullThreadLinkClick) {
        onOpenFullThreadLinkClick(LinkType.OPEN_FULL_COMMENT_THREAD, clickType, event);
      }
    };
  };
  useEffect(function () {
    if (issueKey) {
      experienceTracker.start({
        name: Experiences.FETCH_JIRA_COMMENT_DATA_IN_CONTEXT,
        id: 'fetchJiraCommentData'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experienceTracker, issueKey]);
  var issueData = useLazyLoadQuery(_mainCommentIssueDataQuery, {
    issueKey: issueKey,
    commentId: commentId,
    cloudId: cloudId
  }, {
    fetchPolicy: 'store-and-network'
  });
  var issueAri = issueData === null || issueData === void 0 || (_issueData$jira = issueData.jira) === null || _issueData$jira === void 0 || (_issueData$jira = _issueData$jira.issueByKey) === null || _issueData$jira === void 0 ? void 0 : _issueData$jira.id;
  var checkResult = useMemo(function () {
    var _issueData$jira2, _issueData$jira3;
    if (!issueAri) {
      return {
        result: 'fail',
        reason: 'issueNotFound'
      };
    }
    var edges = issueData === null || issueData === void 0 || (_issueData$jira2 = issueData.jira) === null || _issueData$jira2 === void 0 || (_issueData$jira2 = _issueData$jira2.issueByKey) === null || _issueData$jira2 === void 0 || (_issueData$jira2 = _issueData$jira2.comments) === null || _issueData$jira2 === void 0 ? void 0 : _issueData$jira2.edges;
    if (!edges) {
      return {
        result: 'fail',
        reason: 'commentNotFound'
      };
    }
    var edgesArray = Array.from(edges);
    if (!edgesArray.some(function (s) {
      var _s$node;
      return (s === null || s === void 0 || (_s$node = s.node) === null || _s$node === void 0 ? void 0 : _s$node.commentId) === commentId;
    })) {
      return {
        result: 'fail',
        reason: 'commentNotFound'
      };
    }
    if (!(issueData !== null && issueData !== void 0 && (_issueData$jira3 = issueData.jira) !== null && _issueData$jira3 !== void 0 && (_issueData$jira3 = _issueData$jira3.issueByKey) !== null && _issueData$jira3 !== void 0 && _issueData$jira3.canAddComments)) {
      return {
        result: 'fail',
        reason: 'noPermission'
      };
    }
    return {
      result: 'success'
    };
  }, [commentId, issueAri, issueData === null || issueData === void 0 || (_issueData$jira4 = issueData.jira) === null || _issueData$jira4 === void 0 || (_issueData$jira4 = _issueData$jira4.issueByKey) === null || _issueData$jira4 === void 0 ? void 0 : _issueData$jira4.canAddComments, issueData === null || issueData === void 0 || (_issueData$jira5 = issueData.jira) === null || _issueData$jira5 === void 0 || (_issueData$jira5 = _issueData$jira5.issueByKey) === null || _issueData$jira5 === void 0 || (_issueData$jira5 = _issueData$jira5.comments) === null || _issueData$jira5 === void 0 ? void 0 : _issueData$jira5.edges]);
  useEffect(function () {
    var _issueData$jira6, _issueData$jira$issue, _issueData$jira7;
    fireJiraIssueCommentLoaded(_objectSpread({
      notification: experienceTrackingAttributes.notification,
      listIndex: experienceTrackingAttributes.listIndex,
      issueKey: issueKey,
      commentId: commentId,
      issueAri: issueAri,
      projectId: issueData === null || issueData === void 0 || (_issueData$jira6 = issueData.jira) === null || _issueData$jira6 === void 0 || (_issueData$jira6 = _issueData$jira6.issueByKey) === null || _issueData$jira6 === void 0 || (_issueData$jira6 = _issueData$jira6.projectField) === null || _issueData$jira6 === void 0 || (_issueData$jira6 = _issueData$jira6.project) === null || _issueData$jira6 === void 0 ? void 0 : _issueData$jira6.projectId,
      projectType: String((_issueData$jira$issue = issueData === null || issueData === void 0 || (_issueData$jira7 = issueData.jira) === null || _issueData$jira7 === void 0 || (_issueData$jira7 = _issueData$jira7.issueByKey) === null || _issueData$jira7 === void 0 || (_issueData$jira7 = _issueData$jira7.projectField) === null || _issueData$jira7 === void 0 || (_issueData$jira7 = _issueData$jira7.project) === null || _issueData$jira7 === void 0 ? void 0 : _issueData$jira7.projectType) !== null && _issueData$jira$issue !== void 0 ? _issueData$jira$issue : '').toLowerCase()
    }, checkResult));
  }, [commentId, checkResult, fireJiraIssueCommentLoaded, issueAri, issueData === null || issueData === void 0 || (_issueData$jira8 = issueData.jira) === null || _issueData$jira8 === void 0 || (_issueData$jira8 = _issueData$jira8.issueByKey) === null || _issueData$jira8 === void 0 || (_issueData$jira8 = _issueData$jira8.projectField) === null || _issueData$jira8 === void 0 || (_issueData$jira8 = _issueData$jira8.project) === null || _issueData$jira8 === void 0 ? void 0 : _issueData$jira8.projectId, issueData === null || issueData === void 0 || (_issueData$jira9 = issueData.jira) === null || _issueData$jira9 === void 0 || (_issueData$jira9 = _issueData$jira9.issueByKey) === null || _issueData$jira9 === void 0 || (_issueData$jira9 = _issueData$jira9.projectField) === null || _issueData$jira9 === void 0 || (_issueData$jira9 = _issueData$jira9.project) === null || _issueData$jira9 === void 0 ? void 0 : _issueData$jira9.projectType, issueKey, experienceTrackingAttributes.listIndex, experienceTrackingAttributes.notification]);
  useEffect(function () {
    if (checkResult.result === 'fail') {
      if (checkResult.reason === 'noPermission') {
        experienceTracker.fail({
          name: Experiences.FETCH_JIRA_COMMENT_DATA_IN_CONTEXT,
          attributes: {
            commentId: commentId
          },
          error: {
            name: 'Permission Error',
            message: "User does not have permission to add reaction for ".concat(commentId)
          }
        });
        return;
      }
      experienceTracker.fail({
        name: Experiences.FETCH_JIRA_COMMENT_DATA_IN_CONTEXT,
        attributes: {
          commentId: commentId
        },
        error: {
          name: 'GraphQL Error',
          message: "Failed to fetch comment data for ".concat(commentId)
        }
      });
      return;
    }
    experienceTracker.succeed({
      name: Experiences.FETCH_JIRA_COMMENT_DATA_IN_CONTEXT,
      attributes: {
        commentId: commentId
      }
    });
  }, [checkResult, commentId, experienceTracker]);
  if (checkResult.result === 'success' && issueAri && issueData !== null && issueData !== void 0 && (_issueData$jira0 = issueData.jira) !== null && _issueData$jira0 !== void 0 && _issueData$jira0.issueByKey) {
    var _issueData$jira1, _comment$node, _issueData$jira10, _issueData$jira11, _issueData$jira12, _issueData$jira13;
    // Find the matching comment and its threadParentId
    var edges = issueData === null || issueData === void 0 || (_issueData$jira1 = issueData.jira) === null || _issueData$jira1 === void 0 || (_issueData$jira1 = _issueData$jira1.issueByKey) === null || _issueData$jira1 === void 0 || (_issueData$jira1 = _issueData$jira1.comments) === null || _issueData$jira1 === void 0 ? void 0 : _issueData$jira1.edges;
    var edgesArray = edges ? Array.from(edges) : [];
    var comment = edgesArray.find(function (edge) {
      var _edge$node;
      return (edge === null || edge === void 0 || (_edge$node = edge.node) === null || _edge$node === void 0 ? void 0 : _edge$node.commentId) === commentId;
    });
    var threadParentId = (comment === null || comment === void 0 || (_comment$node = comment.node) === null || _comment$node === void 0 ? void 0 : _comment$node.threadParentId) || commentId;
    return /*#__PURE__*/React.createElement(Inline, {
      alignBlock: "center",
      spread: "space-between",
      testId: "jira-react-reply-bar"
    }, /*#__PURE__*/React.createElement(Inline, {
      space: "space.100",
      alignBlock: "center"
    }, FeatureGates.getExperimentValue('jira-comment-reactions-from-notification-draw', 'isEnabled', false) && /*#__PURE__*/React.createElement(JiraReactBar, {
      contentLink: contentLink,
      issueKey: issueKey,
      issueAri: issueAri,
      projectId: ((_issueData$jira10 = issueData.jira) === null || _issueData$jira10 === void 0 || (_issueData$jira10 = _issueData$jira10.issueByKey) === null || _issueData$jira10 === void 0 || (_issueData$jira10 = _issueData$jira10.projectField) === null || _issueData$jira10 === void 0 || (_issueData$jira10 = _issueData$jira10.project) === null || _issueData$jira10 === void 0 ? void 0 : _issueData$jira10.projectId) || undefined,
      projectType: ((_issueData$jira11 = issueData.jira) === null || _issueData$jira11 === void 0 || (_issueData$jira11 = _issueData$jira11.issueByKey) === null || _issueData$jira11 === void 0 || (_issueData$jira11 = _issueData$jira11.projectField) === null || _issueData$jira11 === void 0 || (_issueData$jira11 = _issueData$jira11.project) === null || _issueData$jira11 === void 0 ? void 0 : _issueData$jira11.projectType) || undefined,
      commentId: commentId,
      cloudId: cloudId,
      emojiProvider: emojiProvider,
      experienceTrackingAttributes: experienceTrackingAttributes,
      handleMarkAsRead: handleMarkAsRead
    }), subProduct !== 'serviceDesk' && /*#__PURE__*/React.createElement(JiraReplyBar, {
      issueAri: issueAri,
      projectId: ((_issueData$jira12 = issueData.jira) === null || _issueData$jira12 === void 0 || (_issueData$jira12 = _issueData$jira12.issueByKey) === null || _issueData$jira12 === void 0 || (_issueData$jira12 = _issueData$jira12.projectField) === null || _issueData$jira12 === void 0 || (_issueData$jira12 = _issueData$jira12.project) === null || _issueData$jira12 === void 0 ? void 0 : _issueData$jira12.projectId) || '',
      projectType: ((_issueData$jira13 = issueData.jira) === null || _issueData$jira13 === void 0 || (_issueData$jira13 = _issueData$jira13.issueByKey) === null || _issueData$jira13 === void 0 || (_issueData$jira13 = _issueData$jira13.projectField) === null || _issueData$jira13 === void 0 || (_issueData$jira13 = _issueData$jira13.project) === null || _issueData$jira13 === void 0 ? void 0 : _issueData$jira13.projectType) || '',
      contentLink: contentLink,
      commentId: commentId,
      cloudId: cloudId,
      isEditorShown: isEditorShown,
      onCommentEditorShown: onCommentEditorShown,
      threadParentId: threadParentId
    })), subProduct !== 'serviceDesk' && /*#__PURE__*/React.createElement(Inline, {
      alignBlock: "center",
      space: "space.050"
    }, /*#__PURE__*/React.createElement(Anchor, {
      href: contentLink,
      xcss: fullThreadLinkStyles,
      testId: "open-full-thread-link",
      onClick: handleOpenFullThreadLinkClick('left'),
      onAuxClick: handleOpenFullThreadLinkClick('middle'),
      onContextMenu: handleOpenFullThreadLinkClick('right')
    }, /*#__PURE__*/React.createElement(FormattedMessage, messages.openFullThread))));
  }
  return null;
};
export default JiraReactReplyBar;